import { createTheme } from "@mui/material/styles";
/**
 * App theme derived from Figma variables for node `87:2813`.
 * Primary: #006FEE
 * Neutrals: Gray/Zinc scale
 * Radius: 8
 */
export const appTheme = createTheme({
    palette: {
        mode: "light",
        primary: {
            main: "#006FEE",
            light: "#CCE3FD",
            contrastText: "#FFFFFF",
        },
        success: {
            main: "#12B76A",
            dark: "#027A48",
        },
        error: {
            main: "#F04438",
            dark: "#B42318",
        },
        background: {
            default: "#F8F9FC", // Blue gray/50
            paper: "#FFFFFF",
        },
        text: {
            primary: "#181D27", // Gray/900
            secondary: "#717680", // Gray/500
        },
        divider: "#E9EAEB", // Gray/200
    },
    shape: {
        borderRadius: 8,
    },
    typography: {
        // Figma uses Inter. We keep a safe fallback stack.
        fontFamily: ["Inter", "system-ui", "Segoe UI", "Roboto", "Arial"].join(","),
    },
    shadows: [
        "none",
        "0px 1px 2px rgba(0,0,0,0.05)", // close to Shadow/xs
        ...Array(23).fill("none"),
    ],
    components: {
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundImage: "none",
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    borderRightColor: "#E9EAEB",
                },
            },
        },
    },
});
// ✅ COLOR PALETTE FOR ALL METRICS
export const METRIC_COLORS = {
    // Shopify
    'shopify_gross_sales': { color: '#EC4899', compareColor: '#F9A8D4' },
    'shopify_revenue': { color: '#3B82F6', compareColor: '#93C5FD' },
    'shopify_orders': { color: '#10B981', compareColor: '#6EE7B7' },
    'shopify_sessions': { color: '#8B5CF6', compareColor: '#C4B5FD' },
    'shopify_aov': { color: '#F59E0B', compareColor: '#FCD34D' },
    'shopify_conv_rate': { color: '#EF4444', compareColor: '#FCA5A5' },
    'shopify_roas': { color: '#6366F1', compareColor: '#A5B4FC' },
    'shopify_discounts': { color: '#EC4899', compareColor: '#F9A8D4' },
    'shopify_discount_per': { color: '#F43F5E', compareColor: '#FDA4AF' },
    'shopify_google_cost': { color: '#F97316', compareColor: '#FDBA74' },
    'shopify_meta_cost': { color: '#0EA5E9', compareColor: '#7DD3FC' },
    'shopify_total_cost': { color: '#14B8A6', compareColor: '#5EEAD4' },
    'shopify_cost_per_session': { color: '#A855F7', compareColor: '#D8B4FE' },
    // Meta
    'meta_revenue': { color: '#EC4899', compareColor: '#F9A8D4' },
    'meta_spend': { color: '#10B981', compareColor: '#6EE7B7' },
    'meta_roas': { color: '#6366F1', compareColor: '#A5B4FC' },
    'meta_clicks': { color: '#0EA5E9', compareColor: '#7DD3FC' },
    'meta_impressions': { color: '#14B8A6', compareColor: '#5EEAD4' },
    'meta_orders': { color: '#F59E0B', compareColor: '#FCD34D' },
    'meta_aov': { color: '#8B5CF6', compareColor: '#C4B5FD' },
    'meta_ctr': { color: '#EF4444', compareColor: '#FCA5A5' },
    'meta_cpc': { color: '#F97316', compareColor: '#FDBA74' },
    'meta_cvr': { color: '#A855F7', compareColor: '#D8B4FE' },
    'meta_octr': { color: '#06B6D4', compareColor: '#67E8F9' },
    'meta_cpoc': { color: '#84CC16', compareColor: '#BEF264' },
    'meta_outboundclicks': { color: '#22C55E', compareColor: '#86EFAC' },
    'meta_reach': { color: '#D946EF', compareColor: '#E879F9' },
    // Google Ads (Adword)
    'adword_revenue': { color: '#F97316', compareColor: '#FDBA74' },
    'adword_spend': { color: '#22C55E', compareColor: '#86EFAC' },
    'adword_roas': { color: '#8B5CF6', compareColor: '#C4B5FD' },
    'adword_clicks': { color: '#3B82F6', compareColor: '#93C5FD' },
    'adword_impressions': { color: '#14B8A6', compareColor: '#5EEAD4' },
    'adword_orders': { color: '#EF4444', compareColor: '#FCA5A5' },
    'adword_aov': { color: '#F59E0B', compareColor: '#FCD34D' },
    'adword_ctr': { color: '#EC4899', compareColor: '#F9A8D4' },
    'adword_cpc': { color: '#6366F1', compareColor: '#A5B4FC' },
    'adword_cvr': { color: '#0EA5E9', compareColor: '#7DD3FC' },
    // GA
    'ga_sessions': { color: '#06B6D4', compareColor: '#67E8F9' },
    'ga_revenue': { color: '#A855F7', compareColor: '#D8B4FE' },
    'ga_orders': { color: '#10B981', compareColor: '#6EE7B7' },
    'ga_aov': { color: '#F59E0B', compareColor: '#FCD34D' },
    'ga_conv_rate': { color: '#EF4444', compareColor: '#FCA5A5' },
    'ga_roas': { color: '#6366F1', compareColor: '#A5B4FC' },
    'ga_google_cost': { color: '#F97316', compareColor: '#FDBA74' },
    'ga_meta_cost': { color: '#EC4899', compareColor: '#F9A8D4' },
    'ga_total_cost': { color: '#3B82F6', compareColor: '#93C5FD' },
    'ga_cost_per_session': { color: '#8B5CF6', compareColor: '#C4B5FD' },
    // Bing
    'bing_revenue': { color: '#0078D4', compareColor: '#5EABEF' },
    'bing_spend': { color: '#00BCF2', compareColor: '#7DD4F9' },
    'bing_roas': { color: '#8661C5', compareColor: '#B89FDB' },
    'bing_clicks': { color: '#00B294', compareColor: '#5ED5C3' },
    'bing_impressions': { color: '#FFB900', compareColor: '#FFD557' },
    'bing_orders': { color: '#E81123', compareColor: '#F1707A' },
    'bing_aov': { color: '#107C10', compareColor: '#54B054' },
    'bing_ctr': { color: '#FF8C00', compareColor: '#FFBC5E' },
    'bing_cpc': { color: '#5C2D91', compareColor: '#9B6DC6' },
    'bing_cvr': { color: '#008575', compareColor: '#4EB8A9' },
    // Criteo
    'criteo_revenue': { color: '#FF5722', compareColor: '#FF8A65' },
    'criteo_spend': { color: '#4CAF50', compareColor: '#81C784' },
    'criteo_roas': { color: '#9C27B0', compareColor: '#BA68C8' },
    'criteo_clicks': { color: '#2196F3', compareColor: '#64B5F6' },
    'criteo_impressions': { color: '#009688', compareColor: '#4DB6AC' },
    'criteo_orders': { color: '#F44336', compareColor: '#E57373' },
    'criteo_aov': { color: '#FFC107', compareColor: '#FFD54F' },
    'criteo_ctr': { color: '#E91E63', compareColor: '#F06292' },
    'criteo_cpc': { color: '#673AB7', compareColor: '#9575CD' },
    'criteo_cvr': { color: '#00BCD4', compareColor: '#4DD0E1' },
    // Klaviyo
    'klaviyo_revenue': { color: '#1A1A1A', compareColor: '#6B6B6B' },
    'klaviyo_email_revenue': { color: '#22C55E', compareColor: '#86EFAC' },
    'klaviyo_sms_revenue': { color: '#3B82F6', compareColor: '#93C5FD' },
    'klaviyo_flow_revenue': { color: '#8B5CF6', compareColor: '#C4B5FD' },
    'klaviyo_open_rate': { color: '#F59E0B', compareColor: '#FCD34D' },
    'klaviyo_click_rate': { color: '#EF4444', compareColor: '#FCA5A5' },
    'klaviyo_recipients': { color: '#EC4899', compareColor: '#F9A8D4' },
    'klaviyo_no_of_emails': { color: '#06B6D4', compareColor: '#67E8F9' },
    'klaviyo_no_of_sms': { color: '#14B8A6', compareColor: '#5EEAD4' },
    'klaviyo_no_of_flows': { color: '#A855F7', compareColor: '#D8B4FE' },
};
// ✅ Default colors if metric not found
export const DEFAULT_COLORS = [
    { color: '#3B82F6', compareColor: '#93C5FD' },
    { color: '#EC4899', compareColor: '#F9A8D4' },
    { color: '#10B981', compareColor: '#6EE7B7' },
    { color: '#F59E0B', compareColor: '#FCD34D' },
    { color: '#8B5CF6', compareColor: '#C4B5FD' },
    { color: '#EF4444', compareColor: '#FCA5A5' },
];
